/** @jsx jsx */

import { find, get, isEmpty, map } from 'lodash';

import { Alert, Margin } from '@zapier/design-system';
import { jsx, css } from '@emotion/core';

import FlowForm from 'app/common/components/FlowForm';
import FormaticConfig from 'app/developer-v3/FormaticConfig';

import type { ErrorList } from 'app/developer-v3/types/validator';
import type { SearchActionSchema } from 'app/developer-v3/platformSchema/search-action';
import type {
  FormifiedDynamicDropdown,
  InfoMeta,
} from 'app/developer-v3/types';

const Styles = {
  resourceInputs: css`
    margin-top: 20px;
  `,
};

const getFieldNameTemplate = (label: string, choices?: string[]) => ({
  type: choices ? 'pretty-select' : 'single-line-string',
  key: 'name',
  label,
  required: true,
  placeholder: 'ex. id',
  help_text: 'Select the data from this trigger to include in the API call.',
  choices,
});

const getDropdownConfig = services => {
  const baseConfig = {
    type: 'pretty-select',
    key: 'source',
    hasSearch: false,
    choices: map(services, service => ({
      value: service.key,
      label: service.display.label,
    })),
  };

  const configs = {
    search: {
      label: 'Search Source',
      help_text: 'Select the search step with the data for this dropdown.',
      placeholder: isEmpty(services) ? 'No Searches' : 'Select Search',
    },
    dynamic: {
      placeholder: isEmpty(services) ? 'No Triggers' : 'Select Trigger',
    },
  };

  return [{ ...baseConfig, ...configs.search }];
};

const getActionHandler = (choices?: string[]) => ({
  help: (
    <Alert status="warning" title="Search Action Needed">
      Add a search action first to find data from your API for this dropdown.
    </Alert>
  ),
  getDropdownConfig,
  fieldsConfig: [getFieldNameTemplate('Search Field Id', choices)],
});

type Props = {
  errors: ErrorList;
  info?: InfoMeta;
  onChange: (options: FormifiedDynamicDropdown) => void;
  searches: Array<SearchActionSchema>;
  value: FormifiedDynamicDropdown;
};

// TODO: Remove this component with SelectSearchOutputFieldZinnia when all its
// consumers are coverted to Zinnia.
function SelectSearchOutputField(props: Props) {
  const { errors, info, onChange, searches, value = {} } = props;

  const source = get(value, ['source'], '');
  const dynamicService = find(searches, { key: source });
  const outputFields = get(dynamicService, ['operation', 'outputFields'], []);
  const outputFieldKeys = map(outputFields, 'key');
  const actionHandler = getActionHandler(
    isEmpty(outputFieldKeys) ? undefined : outputFieldKeys
  );

  const onDropdownChange = valueDropdown => {
    onChange(valueDropdown);
  };

  const onTextChange = ({ name, label }) => {
    if (value) {
      onChange({ source: value.source, name, label });
    }
  };

  return (
    <div>
      <FlowForm
        config={FormaticConfig}
        fields={actionHandler.getDropdownConfig(searches)}
        isValid={true}
        meta={{ errors, info }}
        onChange={onDropdownChange}
        value={value}
      />
      {isEmpty(searches) ? (
        <Margin margin="20px 0px 0px 0px">{actionHandler.help}</Margin>
      ) : (
        !!source && (
          <FlowForm
            config={FormaticConfig}
            css={Styles.resourceInputs}
            fields={actionHandler.fieldsConfig}
            isValid={true}
            meta={{ errors, info }}
            onChange={onTextChange}
            value={value}
          />
        )
      )}
    </div>
  );
}

export default SelectSearchOutputField;
